import { Link } from "react-router-dom";
import { Phone, Smartphone, Mail, MapPin } from "lucide-react";
import { COMPANY, wa, WA_MESSAGES } from "@/data/site";
import { track } from "@/lib/analytics";
import { useQuote } from "@/components/QuoteModal";

const productLinks = [
  { label: "Computers & Assembled PCs", to: "/products/assembled-computers" },
  { label: "Printers", to: "/products/printers" },
  { label: "Toners & Consumables", to: "/products/toners" },
  { label: "Computer Components & Accessories", to: "/products/components" },
  { label: "Networking Solutions", to: "/products/networking" },
  { label: "CCTV & Surveillance", to: "/products/cctv" },
  { label: "UPS & Power", to: "/products/ups" },
];

const serviceLinks = [
  { label: "Computer & Laptop Service", to: "/services/computer-service" },
  { label: "Installation & Configuration", to: "/services/installation" },
  { label: "Networking Services", to: "/services/networking" },
  { label: "CCTV Installation", to: "/services/cctv-installation" },
  { label: "Solar Energy Solutions", to: "/services/solar", badge: "Coming Soon" },
  { label: "AMC", to: "/amc", badge: "Coming Soon" },
];

const solutionLinks = [
  { label: "Small Business", to: "/business-solutions/small-business" },
  { label: "Offices", to: "/business-solutions/offices" },
  { label: "Hospitals & Clinics", to: "/business-solutions/hospitals" },
  { label: "Banks & Financial Offices", to: "/business-solutions/banks" },
  { label: "Institutions", quote: "Institution / Institutional Requirement" },
];

const FooterCol = ({ title, links }) => {
  const { openQuote } = useQuote();
  return (
    <div>
      <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">{title}</h3>
      <ul className="space-y-2.5">
        {links.map((l) =>
          l.quote ? (
            <li key={l.label}>
              <button
                onClick={() => openQuote(l.quote)}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors text-left"
              >
                {l.label}
              </button>
            </li>
          ) : (
            <li key={l.label}>
              <Link
                to={l.to}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                {l.label}
                {l.badge && <span className="text-[10px] tracking-wide text-white/40 normal-case">({l.badge})</span>}
              </Link>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-cts-charcoal text-white">
    <div className="container-site py-12 md:py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-[1.2fr_0.9fr_0.9fr_0.9fr_1.3fr]">
      <div>
        <div className="inline-block bg-white rounded-md px-3 py-2 mb-5">
          <img src="/cts-logo.png" alt="Cherians Tech Solutions logo" className="h-12 w-auto" />
        </div>
        <p className="font-heading font-bold text-sm tracking-wide text-white/90 mb-1">{COMPANY.triad}</p>
        <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5">People | Solutions | Progress</p>
        <a
          href={COMPANY.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-address-link"
          className="flex items-start gap-2.5 text-sm text-white/70 hover:text-white transition-colors"
        >
          <MapPin size={16} className="mt-0.5 shrink-0 text-cts-red" />
          <address className="not-italic leading-relaxed">
            {COMPANY.name}
            <br />
            {COMPANY.addressLines.map((line) => (
              <span key={line}>
                {line}
                <br />
              </span>
            ))}
          </address>
        </a>
      </div>
      <FooterCol title="Products" links={productLinks} />
      <FooterCol title="Services" links={serviceLinks} />
      <FooterCol title="Business Solutions" links={solutionLinks} />
      <div className="min-w-0">
        <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">Contact</h3>
        <ul className="space-y-3 text-sm">
          <li>
            <a href={COMPANY.phoneHref} onClick={() => track("phone_click", { location: "footer" })} data-testid="footer-phone-link" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <Phone size={16} className="text-cts-red shrink-0" /> {COMPANY.phone}
            </a>
          </li>
          <li>
            <a href={COMPANY.mobileHref} onClick={() => track("phone_click", { location: "footer" })} data-testid="footer-mobile-link" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <Smartphone size={16} className="text-cts-red shrink-0" /> {COMPANY.mobile}
            </a>
          </li>
          <li>
            <a href={wa(WA_MESSAGES.general)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "footer" })} data-testid="footer-whatsapp-link" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <Smartphone size={16} className="text-cts-red shrink-0" /> WhatsApp
            </a>
          </li>
          <li>
            <a href={`mailto:${COMPANY.email}`} data-testid="footer-email-link" className="flex items-start gap-2.5 text-white/70 hover:text-white transition-colors text-[13px] break-words min-w-0">
              <Mail size={16} className="text-cts-red shrink-0 mt-0.5" /> <span className="min-w-0">{COMPANY.email}</span>
            </a>
          </li>
        </ul>
        <p className="mt-5 text-xs text-white/50">GST: {COMPANY.gst}</p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="container-site py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
        <p>© 2026 {COMPANY.name}. All rights reserved.</p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <Link to="/about" data-testid="footer-bottom-about-link" className="hover:text-white transition-colors">About</Link>
          <Link to="/projects" data-testid="footer-bottom-projects-link" className="hover:text-white transition-colors">Projects</Link>
          <Link to="/technology-advice" data-testid="footer-bottom-tech-advice-link" className="hover:text-white transition-colors">Technology Advice</Link>
          <Link to="/podcasts" data-testid="footer-bottom-podcasts-link" className="hover:text-white transition-colors">Podcasts</Link>
          <Link to="/reviews" data-testid="footer-bottom-reviews-link" className="hover:text-white transition-colors">Reviews</Link>
          <Link to="/contact" data-testid="footer-bottom-contact-link" className="hover:text-white transition-colors">Contact</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  </footer>
);
